// Tab "Dados & Tema" (§6.2) — wave 2 EDITÁVEL: form RHF+zod
// (identidade + toggles + 5 cores com picker/hex), logo via
// FileUploader (opcional — sem arquivo = campo não enviado), preview
// do checkout AO VIVO (useWatch) e dirty-state footer
// (Descartar/Salvar). Alterar o slug exige AlertDialog de aviso
// ("pode quebrar links/integrações", débito §7.1-3); o toggle de
// active carrega tooltip "flag informativo" (débito §7.1-2).
import { zodResolver } from "@hookform/resolvers/zod"
import { UploadCloudIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  FileInput,
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
} from "@/components/ui/file-upload"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { useUpdateAdminWhiteLabelMutation } from "@/services/admin"
import type {
  AdminWhiteLabelDetail,
  AdminWhiteLabelUpdatePayload,
} from "@/types/admin-white-labels"

import {
  buildThemeFormSchema,
  HEX_COLOR_PATTERN,
  themeFormDefaults,
  type ThemeFormValues,
} from "../schemas"
import { COLOR_KEYS, type ColorKey } from "../utils"
import { FieldInfo } from "./field-info"
import { ThemePreview } from "./theme-preview"

const panelPrefix = "pages.adminWhiteLabels"

// Limite do upload de logo (o BFF recebe base64 em JSON).
const LOGO_MAX_SIZE_BYTES = 1024 * 1024

// Lê o arquivo como data-URI (o backend aceita data-URI ou base64).
const readFileAsDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve(String(reader.result ?? ""))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

// "" no form → null no payload (semântica merge do backend).
const toColorPayload = (
  colors: ThemeFormValues["colors"]
): AdminWhiteLabelUpdatePayload["colors"] => ({
  lineBackground: colors.lineBackground || null,
  buttonActionBackground:
    colors.buttonActionBackground || null,
  buttonActionBackgroundHover:
    colors.buttonActionBackgroundHover || null,
  buttonActionText: colors.buttonActionText || null,
  background: colors.background || null,
})

type ThemeTabProps = {
  detail: AdminWhiteLabelDetail
}

export const ThemeTab = ({ detail }: ThemeTabProps) => {
  const { t } = useTranslation()
  const updateMutation = useUpdateAdminWhiteLabelMutation(
    detail.uuid
  )

  // Logo novo (opcional): arquivo do FileUploader + data-URI lido.
  const [logoFiles, setLogoFiles] = useState<File[] | null>(
    null
  )
  const [logoDataUri, setLogoDataUri] = useState<
    string | null
  >(null)
  // Valores aguardando a confirmação da troca de slug.
  const [pendingValues, setPendingValues] =
    useState<ThemeFormValues | null>(null)

  const schema = useMemo(
    () =>
      buildThemeFormSchema({
        required: t(`${panelPrefix}.form.required`),
        invalidHex: t(`${panelPrefix}.form.invalidHex`),
      }),
    [t]
  )

  const form = useForm<ThemeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: themeFormDefaults(detail),
  })

  // Preview AO VIVO: reage ao form (cores + nome) e ao logo novo.
  const watchedColors = useWatch({
    control: form.control,
    name: "colors",
  })
  const watchedName = useWatch({
    control: form.control,
    name: "name",
  })

  const isDirty =
    form.formState.isDirty || logoDataUri !== null

  const handleLogoChange = async (files: File[] | null) => {
    setLogoFiles(files)
    const file = files?.[0]
    if (!file) {
      setLogoDataUri(null)
      return
    }
    setLogoDataUri(await readFileAsDataUri(file))
  }

  const save = (values: ThemeFormValues) => {
    const payload: AdminWhiteLabelUpdatePayload = {
      name: values.name,
      description: values.description,
      slug: values.slug,
      principal: values.principal,
      active: values.active,
      // Sem arquivo novo o campo NÃO vai no payload (mantém o logo).
      ...(logoDataUri ? { logo: logoDataUri } : {}),
      colors: toColorPayload(values.colors),
    }
    updateMutation.mutate(payload, {
      onSuccess: () => {
        // Consolida o form no estado salvo (limpa o dirty).
        form.reset(values)
        setLogoFiles(null)
        setLogoDataUri(null)
      },
    })
  }

  // Slug alterado → AlertDialog de aviso antes de salvar.
  const handleSubmit = form.handleSubmit((values) => {
    if (values.slug !== detail.slug) {
      setPendingValues(values)
      return
    }
    save(values)
  })

  const handleDiscard = () => {
    form.reset(themeFormDefaults(detail))
    setLogoFiles(null)
    setLogoDataUri(null)
  }

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        noValidate
      >
        <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t(`${panelPrefix}.theme.identityTitle`)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1.5">
                        <FormLabel>
                          {t(
                            `${panelPrefix}.theme.nameLabel`
                          )}
                        </FormLabel>
                        <FieldInfo
                          hintKey={`${panelPrefix}.theme.descriptions.name`}
                        />
                      </div>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1.5">
                        <FormLabel>
                          {t(
                            `${panelPrefix}.theme.descriptionLabel`
                          )}
                        </FormLabel>
                        <FieldInfo
                          hintKey={`${panelPrefix}.theme.descriptions.description`}
                        />
                      </div>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1.5">
                        <FormLabel>
                          {t(
                            `${panelPrefix}.theme.slugLabel`
                          )}
                        </FormLabel>
                        <FieldInfo
                          hintKey={`${panelPrefix}.theme.descriptions.slug`}
                        />
                      </div>
                      <FormControl>
                        <Input
                          className="font-mono"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex flex-wrap gap-6">
                  <FormField
                    control={form.control}
                    name="principal"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            aria-label={t(
                              `${panelPrefix}.theme.principalLabel`
                            )}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer font-normal">
                          {t(
                            `${panelPrefix}.theme.principalLabel`
                          )}
                        </FormLabel>
                        <FieldInfo
                          hintKey={`${panelPrefix}.theme.descriptions.principal`}
                        />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            aria-label={t(
                              `${panelPrefix}.theme.activeLabel`
                            )}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer font-normal">
                          {t(
                            `${panelPrefix}.theme.activeLabel`
                          )}
                        </FormLabel>
                        {/* Débito §7.1-2: flag sem enforcement (activeHint). */}
                        <FieldInfo
                          hintKey={`${panelPrefix}.theme.activeHint`}
                        />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <span className="text-sm font-medium">
                    {t(`${panelPrefix}.theme.logoLabel`)}
                  </span>
                  <FileUploader
                    value={logoFiles}
                    onValueChange={handleLogoChange}
                    dropzoneOptions={{
                      accept: {
                        "image/*": [
                          ".jpg",
                          ".jpeg",
                          ".png",
                          ".gif",
                          ".svg",
                        ],
                      },
                      maxFiles: 1,
                      multiple: false,
                      maxSize: LOGO_MAX_SIZE_BYTES,
                    }}
                  >
                    <FileInput className="rounded-lg border border-dashed">
                      <div className="flex flex-col items-center gap-1 py-4 text-center">
                        <UploadCloudIcon
                          className="size-5 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <span className="text-xs text-muted-foreground">
                          {t(
                            `${panelPrefix}.theme.logoDropHint`
                          )}
                        </span>
                      </div>
                    </FileInput>
                    <FileUploaderContent>
                      {logoFiles?.map((file, index) => (
                        <FileUploaderItem
                          key={file.name}
                          index={index}
                        >
                          <span className="truncate text-xs">
                            {file.name}
                          </span>
                        </FileUploaderItem>
                      ))}
                    </FileUploaderContent>
                  </FileUploader>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t(`${panelPrefix}.theme.colorsTitle`)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {COLOR_KEYS.map((key) => (
                  <ColorField key={key} colorKey={key} />
                ))}
              </CardContent>
            </Card>
          </div>

          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-base">
                {t(`${panelPrefix}.theme.previewTitle`)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ThemePreview
                name={watchedName || detail.name}
                logoSrc={logoDataUri ?? detail.logoUrl}
                colors={watchedColors}
              />
              <p className="text-xs text-muted-foreground">
                {t(`${panelPrefix}.theme.previewHint`)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Dirty-state footer (Descartar/Salvar). */}
        {isDirty && (
          <div
            data-testid="theme-dirty-footer"
            className="sticky bottom-0 flex items-center justify-end gap-2 rounded-xl border bg-card p-3 shadow-sm"
          >
            <Button
              type="button"
              variant="outline"
              disabled={updateMutation.isPending}
              onClick={handleDiscard}
            >
              {t(`${panelPrefix}.form.discard`)}
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
            >
              {t(`${panelPrefix}.form.save`)}
            </Button>
          </div>
        )}
      </form>

      {/* Aviso da troca de slug (débito §7.1-3). */}
      <AlertDialog
        open={pendingValues !== null}
        onOpenChange={(open) => {
          if (!open) setPendingValues(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${panelPrefix}.theme.slugDialog.title`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${panelPrefix}.theme.slugDialog.description`,
                {
                  from: detail.slug,
                  to: pendingValues?.slug ?? "",
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${panelPrefix}.theme.slugDialog.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingValues) save(pendingValues)
                setPendingValues(null)
              }}
            >
              {t(`${panelPrefix}.theme.slugDialog.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  )
}

// Campo de cor: swatch <input type="color"> sincronizado + hex.
const ColorField = ({
  colorKey,
}: {
  colorKey: ColorKey
}) => {
  const { t } = useTranslation()
  return (
    <FormField
      name={`colors.${colorKey}`}
      render={({ field }) => {
        const isValidHex = HEX_COLOR_PATTERN.test(
          field.value ?? ""
        )
        return (
          <FormItem>
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-1 items-center gap-1.5">
                <FormLabel>
                  {t(
                    `${panelPrefix}.theme.colors.${colorKey}`
                  )}
                </FormLabel>
                <FieldInfo
                  hintKey={`${panelPrefix}.theme.descriptions.colors.${colorKey}`}
                />
              </div>
              <FormControl>
                <Input
                  className="w-32 font-mono text-xs"
                  placeholder="#RRGGBB"
                  {...field}
                />
              </FormControl>
              {/* Picker nativo — só escreve hex válido. */}
              <input
                type="color"
                data-testid={`color-picker-${colorKey}`}
                aria-label={t(
                  `${panelPrefix}.theme.pickColor`,
                  {
                    color: t(
                      `${panelPrefix}.theme.colors.${colorKey}`
                    ),
                  }
                )}
                className="h-8 w-8 shrink-0 cursor-pointer rounded border bg-transparent p-0.5"
                value={isValidHex ? field.value : "#ffffff"}
                onChange={(event) =>
                  field.onChange(event.target.value)
                }
              />
            </div>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
